/**
 * @module utils/membershipDetails/adapters/dyntech
 * @description Único archivo del front que conoce la forma del payload de
 * Dyntech dentro de `membership.externalPlan` (typed: numeroGrupo,
 * numeroSocio; sin tipar: `raw` = última `condicionSocio`). `raw` no está
 * controlado por nosotros, así que cada campo se lee defensivamente y una
 * fila sin dato se omite.
 */

/**
 * @description ¿Es un objeto plano (no null, no array)?
 * @param {*} value
 * @returns {boolean}
 */
const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * @description String no vacío, o `null`.
 * @param {*} value
 * @returns {string|null}
 */
const toText = (value) => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text || null;
};

/**
 * @description Formatea una fecha ISO de Dyntech (ej. "2007-11-01T00:00:00",
 * sin zona horaria) como dd/MM/yyyy leyendo el string, sin pasar por `Date`
 * para que la zona horaria del navegador no corra el día.
 * @param {*} value
 * @returns {string|null} `null` si no es una fecha ISO válida
 */
const toDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(toText(value) || "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
};

/**
 * @description Lista de códigos como texto "a, b". Acepta un string suelto
 * por si Dyntech lo manda así en vez de array.
 * @param {*} value
 * @returns {string|null}
 */
const toList = (value) => {
  const items = (Array.isArray(value) ? value : [value])
    .map(toText)
    .filter(Boolean);
  return items.length ? items.join(", ") : null;
};

/**
 * @description Sí/No para un booleano; `null` si no lo es.
 * @param {*} value
 * @returns {string|null}
 */
const toYesNo = (value) =>
  typeof value === "boolean" ? (value ? "Sí" : "No") : null;

/** @description Formateador por tipo de campo; sin tipo, texto plano. */
const FORMATTERS = { date: toDate, list: toList, bool: toYesNo };

/**
 * @description Lista explícita de los campos de `condicionSocio` que
 * mostramos, con etiqueta y tipo. Todo lo que no esté acá no se muestra
 * (ej. `estadoMorosidad`, `guid`). Credencial y validador leen esta tabla;
 * `credential: true` marca los que van además en la credencial. El orden es
 * el de pantalla.
 * @type {Array<{key: string, label: string, kind?: 'date'|'list'|'bool', credential?: boolean}>}
 */
const FIELDS = [
  { key: "plan", label: "Plan" },
  { key: "obraSocial", label: "Obra social" },
  {
    key: "planAdicional",
    label: "Plan adicional",
    kind: "list",
    credential: true,
  },
  { key: "condicionIva", label: "Condición de IVA", credential: true },
  { key: "fechaVigencia", label: "Vigencia", kind: "date", credential: true },
  { key: "fechaEgreso", label: "Fecha de egreso", kind: "date" },
  { key: "habilitado", label: "Habilitado", kind: "bool" },
  { key: "leyenda", label: "Leyenda" },
];

/**
 * @description Filas label/valor de los campos de `FIELDS` indicados.
 * @param {Object} raw - `condicionSocio`
 * @param {Array} fields - Subconjunto de `FIELDS`
 * @returns {Array<{key: string, label: string, value: string}>} sin filas vacías
 */
const toRows = (raw, fields) =>
  fields
    .map(({ key, label, kind }) => ({
      key,
      label,
      value: (FORMATTERS[kind] || toText)(raw[key]),
    }))
    .filter((row) => row.value);

/**
 * @description Detalles de credencial para un Membership de motor Dyntech.
 * @param {Object} membership - Membership vigente (con engine === 'dyntech')
 * @returns {{memberNumber: string|null, rows: Array<{key: string, label: string, value: string}>}}
 */
export default function dyntechDetails(membership) {
  const externalPlan = isPlainObject(membership.externalPlan)
    ? membership.externalPlan
    : {};
  const raw = isPlainObject(externalPlan.raw) ? externalPlan.raw : {};

  const grupo = externalPlan.numeroGrupo;
  const socio = toText(externalPlan.numeroSocio);
  const memberNumber =
    grupo !== null && grupo !== undefined && grupo !== "" && socio
      ? `${grupo}:${socio}`
      : null;

  return {
    memberNumber,
    rows: toRows(
      raw,
      FIELDS.filter((field) => field.credential)
    ),
  };
}

/**
 * @description Filas del detalle del afiliado en el validador, a partir de
 * `engine.details` de `POST /validations/validate`: la respuesta cruda de
 * Dyntech `{ verificacion, condicion }`. Solo `condicion` (`condicionSocio`)
 * tiene detalle; si falta (identidad no verificada) no hay filas. Muestra solo
 * los campos de `FIELDS`, con su etiqueta y orden; las filas sin dato se omiten.
 * @param {*} details - `engine.details` de la respuesta de validación
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function validationDetails(details) {
  const condicion = isPlainObject(details) ? details.condicion : null;
  return isPlainObject(condicion) ? toRows(condicion, FIELDS) : [];
}
